import { useState } from 'react';
import { db, storage } from '../firebase';
import logger from '../utils/logger';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  onSnapshot,
  serverTimestamp,
  doc,
  setDoc,
  getDoc,
  updateDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

/**
 * useMessaging - Manages direct messaging state and operations.
 *
 * Handles conversations, messages, sending DMs, image uploads,
 * user search, and unread message tracking.
 */
export const useMessaging = ({ currentUser, blockedUsers }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newDM, setNewDM] = useState('');
  const [dmRecipient, setDmRecipient] = useState(null);
  const [unreadDMs, setUnreadDMs] = useState(0);
  const [dmImage, setDmImage] = useState(null);
  const [dmImagePreview, setDmImagePreview] = useState(null);
  const [sendingDM, setSendingDM] = useState(false);
  const [suggestedDMUsers, setSuggestedDMUsers] = useState([]);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');

  const sendDM = async (recipientId) => {
    if ((!newDM.trim() && !dmImage) || !currentUser || !recipientId || sendingDM) {
      return;
    }

    if (blockedUsers.includes(recipientId)) {
      alert('You cannot send messages to users you have blocked.');
      return;
    }

    setSendingDM(true);
    try {
      const conversationId = [currentUser.uid, recipientId].sort().join('_');

      let imageUrl = null;
      if (dmImage) {
        const imageRef = ref(storage, `dm-images/${currentUser.uid}/${Date.now()}_${dmImage.name}`);
        const snapshot = await uploadBytes(imageRef, dmImage);
        imageUrl = await getDownloadURL(snapshot.ref);
      }

      const messageContent = newDM.trim() || (imageUrl ? 'Sent an image' : '');

      await addDoc(collection(db, 'messages'), {
        conversationId,
        senderId: currentUser.uid,
        senderName: currentUser.username,
        senderAvatar: currentUser.avatar,
        recipientId,
        content: messageContent,
        imageUrl,
        read: false,
        timestamp: serverTimestamp(),
      });

      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);

      if (conversationDoc.exists()) {
        await updateDoc(conversationRef, {
          lastMessage: messageContent,
          lastMessageTime: serverTimestamp(),
          [`unreadCount_${recipientId}`]: (conversationDoc.data()[`unreadCount_${recipientId}`] || 0) + 1,
        });
      } else {
        const recipientDoc = await getDoc(doc(db, 'users', recipientId));
        const recipientData = recipientDoc.data();

        await setDoc(conversationRef, {
          participants: [currentUser.uid, recipientId],
          participantData: {
            [currentUser.uid]: {
              username: currentUser.username,
              handle: currentUser.handle,
              avatar: currentUser.avatar,
            },
            [recipientId]: {
              username: recipientData.username,
              handle: recipientData.handle,
              avatar: recipientData.avatar,
            },
          },
          lastMessage: messageContent,
          lastMessageTime: serverTimestamp(),
          [`unreadCount_${recipientId}`]: 1,
          [`unreadCount_${currentUser.uid}`]: 0,
          createdAt: serverTimestamp(),
        });
      }

      setNewDM('');
      setDmImage(null);
      setDmImagePreview(null);
    } catch (err) {
      logger.error('Error sending DM:', err);
      alert(`Failed to send message: ${err.message}`);
    } finally {
      setSendingDM(false);
    }
  };

  const markMessagesAsRead = async (conversationId) => {
    if (!currentUser || !conversationId) return;

    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        [`unreadCount_${currentUser.uid}`]: 0,
      });

      const q = query(
        collection(db, 'messages'),
        where('conversationId', '==', conversationId),
        where('recipientId', '==', currentUser.uid),
        where('read', '==', false)
      );

      try {
        const snapshot = await getDocs(q);
        const batch = [];
        snapshot.forEach((msgDoc) => {
          batch.push(updateDoc(msgDoc.ref, { read: true }));
        });
        await Promise.all(batch);
      } catch (err) {
        logger.log('No messages to mark as read or query failed:', err);
      }
    } catch (err) {
      logger.error('Error marking messages as read:', err);
    }
  };

  const loadConversations = () => {
    if (!currentUser) return;

    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', currentUser.uid)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const conversationsData = [];
        snapshot.forEach((convDoc) => {
          const data = convDoc.data();
          const otherUserId = data.participants.find((id) => id !== currentUser.uid);

          conversationsData.push({
            id: convDoc.id,
            ...data,
            otherUser: data.participantData
              ? { ...data.participantData[otherUserId], uid: otherUserId }
              : null,
            otherUserId,
            unreadCount: data[`unreadCount_${currentUser.uid}`] || 0,
          });
        });

        const filteredConversations = conversationsData.filter(
          (conv) => !blockedUsers.includes(conv.otherUserId)
        );

        filteredConversations.sort((a, b) => {
          const aTime = a.lastMessageTime?.toMillis() || 0;
          const bTime = b.lastMessageTime?.toMillis() || 0;
          return bTime - aTime;
        });

        const totalUnread = filteredConversations.reduce(
          (sum, conv) => sum + (conv.unreadCount || 0),
          0
        );
        setUnreadDMs(totalUnread);
        setConversations(filteredConversations);
      },
      (err) => {
        logger.error('Error loading conversations:', err);
        setConversations([]);
      }
    );

    return unsubscribe;
  };

  const loadMessages = (conversationId) => {
    if (!conversationId) return;

    const q = query(
      collection(db, 'messages'),
      where('conversationId', '==', conversationId)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const messagesData = [];
        snapshot.forEach((msgDoc) => {
          messagesData.push({
            id: msgDoc.id,
            ...msgDoc.data(),
          });
        });

        messagesData.sort((a, b) => {
          const aTime = a.timestamp?.toMillis() || 0;
          const bTime = b.timestamp?.toMillis() || 0;
          return aTime - bTime;
        });

        setMessages(messagesData);
      },
      (err) => {
        logger.error('Error loading messages:', err);
        setMessages([]);
      }
    );

    return unsubscribe;
  };

  const createNewConversation = async (recipientUser) => {
    if (!currentUser || !recipientUser) return;

    if (!recipientUser.uid) {
      logger.error('Recipient user missing uid:', recipientUser);
      alert('Error: Invalid user data. Please try again.');
      return;
    }

    try {
      const conversationId = [currentUser.uid, recipientUser.uid].sort().join('_');

      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);

      if (!conversationDoc.exists()) {
        await setDoc(conversationRef, {
          participants: [currentUser.uid, recipientUser.uid],
          participantData: {
            [currentUser.uid]: {
              username: currentUser.username,
              handle: currentUser.handle,
              avatar: currentUser.avatar,
            },
            [recipientUser.uid]: {
              username: recipientUser.username,
              handle: recipientUser.handle,
              avatar: recipientUser.avatar,
            },
          },
          lastMessage: '',
          lastMessageTime: serverTimestamp(),
          [`unreadCount_${recipientUser.uid}`]: 0,
          [`unreadCount_${currentUser.uid}`]: 0,
          createdAt: serverTimestamp(),
        });
      }

      setDmRecipient(recipientUser);
      setSelectedConversation({
        id: conversationId,
        participants: [currentUser.uid, recipientUser.uid],
        participantData: {
          [currentUser.uid]: {
            username: currentUser.username,
            handle: currentUser.handle,
            avatar: currentUser.avatar,
          },
          [recipientUser.uid]: {
            username: recipientUser.username,
            handle: recipientUser.handle,
            avatar: recipientUser.avatar,
          },
        },
      });

      setMessages([]);
      loadMessages(conversationId);
    } catch (err) {
      logger.error('Error in createNewConversation:', err);
      alert(`Failed to open conversation: ${err.message}`);
    }
  };

  const searchUsers = async (searchQuery) => {
    if (!searchQuery.trim()) {
      setUserSearchResults([]);
      return;
    }

    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);

      const results = [];
      snapshot.forEach((userDoc) => {
        const userData = userDoc.data();
        const uid = userDoc.id;

        if (uid === currentUser?.uid) return;

        const username = userData.username?.toLowerCase() || '';
        const handle = userData.handle?.toLowerCase() || '';
        const q = searchQuery.toLowerCase();

        if (username.includes(q) || handle.includes(q)) {
          results.push({ uid, ...userData });
        }
      });

      setUserSearchResults(results.slice(0, 10));
    } catch (err) {
      logger.error('Error searching users:', err);
    }
  };

  const handleDMImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image must be less than 5MB');
        return;
      }
      setDmImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setDmImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return {
    // State
    conversations,
    setConversations,
    selectedConversation,
    setSelectedConversation,
    messages,
    setMessages,
    newDM,
    setNewDM,
    dmRecipient,
    setDmRecipient,
    unreadDMs,
    setUnreadDMs,
    dmImage,
    setDmImage,
    dmImagePreview,
    setDmImagePreview,
    sendingDM,
    suggestedDMUsers,
    setSuggestedDMUsers,
    userSearchResults,
    setUserSearchResults,
    userSearchQuery,
    setUserSearchQuery,

    // Handlers
    sendDM,
    markMessagesAsRead,
    loadConversations,
    loadMessages,
    createNewConversation,
    searchUsers,
    handleDMImageSelect,
  };
};
